import React, { useCallback, useEffect, useState } from 'react';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { DownloadFormat } from '../../../../../../backend/src/types/types';
import { CHIP_COLOURS } from '../../../lib/chip-colours';
import { FilterField } from '@/api/queries/useGetMetadataFilterOptions';
import { cn } from '@/helpers/shadCnUtils';
import { usePermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import {
  MultiSelect,
  MultiSelectItem,
  MultiSelectContent,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/new/components/shadcn/multi-select';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
} from '@/new/components/shadcn/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

const FORMAT_OPTIONS = [DownloadFormat.EXCEL, DownloadFormat.DOCX];

interface Props {
  columnOptions: string[];
  relativityColumnOptions?: FilterField[];
  downloadTitle: string;
  loadingDownload: boolean;
  totalCount?: number;
  showSourceDocumentsOption?: boolean;
  handleDownload: ({
    title,
    columns,
    relativityColumns,
    downloadFormat,
    includeSourceDocuments,
  }: {
    title: string;
    columns: string[];
    relativityColumns?: string[];
    downloadFormat: DownloadFormat;
    includeSourceDocuments?: boolean;
  }) => void;
  showEmailNotification?: boolean;
}

export const DownloadTableModal = ({
  columnOptions,
  relativityColumnOptions,
  downloadTitle,
  loadingDownload,
  totalCount,
  showSourceDocumentsOption = false,
  showEmailNotification = false,
  handleDownload,
}: Props) => {
  const { allowed: canDownloadDocs } = usePermission(Permission.DocumentDownload);
  const [title, setTitle] = useState(downloadTitle);
  const [columns, setColumns] = useState<string[]>(columnOptions);
  const [selectedRelativityColumns, setSelectedRelativityColumns] = useState<string[]>([]);
  const [downloadFormat, setDownloadFormat] = useState<DownloadFormat>(FORMAT_OPTIONS[0]);
  const [includeSourceDocuments, setIncludeSourceDocuments] = useState(false);

  useEffect(() => {
    setTitle(downloadTitle);
  }, [downloadTitle]);

  const handleChangeTitle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const handleChangeSelectedColumns = useCallback((values: string[]) => {
    setColumns(values);
  }, []);

  const handleChangeSelectedRelativityColumns = useCallback((values: string[]) => {
    setSelectedRelativityColumns(values);
  }, []);

  const handleChangeSelectedFormat = useCallback((newValue: string) => {
    setDownloadFormat(newValue as DownloadFormat);
  }, []);

  const handleDownloadClick = () => {
    if (!downloadFormat) return;
    handleDownload({
      title,
      columns: columns,
      relativityColumns: selectedRelativityColumns.length > 0 ? selectedRelativityColumns : undefined,
      downloadFormat: downloadFormat,
      includeSourceDocuments,
    });
  };

  return (
    <>
      {loadingDownload ? (
        <div className="flex h-72 flex-col items-center justify-center gap-8">
          <div className="text-xl font-medium text-gray-700">Preparing your download...</div>
          <Icon className="lucide--loader-circle animate-spin text-4xl text-teal-500" />
        </div>
      ) : (
        <div>
          {totalCount !== undefined && (
            <div className="mb-4 flex items-center gap-1 rounded-md bg-blue-50 p-3 text-xs text-blue-600">
              <Icon className="lucide--info h-3 w-3 text-blue-600" />
              This download will include <span className="font-semibold">{totalCount}</span>{' '}
              {totalCount === 1 ? 'item' : 'items'}
            </div>
          )}
          <div>
            <div className="mb-4">
              <Label htmlFor="title" className="text-sm font-medium text-gray-700">
                File Name
              </Label>
              <Input
                id="title"
                onChange={handleChangeTitle}
                value={title}
                placeholder="Enter a title for your file"
                className="w-full"
              />
            </div>

            <div className="mb-4">
              <Label className="text-sm font-medium text-gray-700">Select Columns</Label>

              <MultiSelect values={columns} onValuesChange={handleChangeSelectedColumns}>
                <MultiSelectTrigger className="w-full">
                  <MultiSelectValue placeholder="Select columns" />
                </MultiSelectTrigger>
                <MultiSelectContent>
                  {columnOptions.map((column) => (
                    <MultiSelectItem key={column} value={column}>
                      {column}
                    </MultiSelectItem>
                  ))}
                </MultiSelectContent>
              </MultiSelect>
            </div>

            {relativityColumnOptions && relativityColumnOptions.length > 0 && (
              <div className="mb-4">
                <Label className="flex items-center gap-2 text-sm font-medium text-purple-500">
                  <Icon className="lucide--database" /> Relativity Metadata Columns
                </Label>

                <MultiSelect values={selectedRelativityColumns} onValuesChange={handleChangeSelectedRelativityColumns}>
                  <MultiSelectTrigger className="w-full">
                    <MultiSelectValue className={CHIP_COLOURS.relativity} placeholder="Select metadata columns" />
                  </MultiSelectTrigger>
                  <MultiSelectContent>
                    {relativityColumnOptions.map((field) => (
                      <MultiSelectItem key={field.fieldName} value={field.fieldName}>
                        {field.displayName}
                      </MultiSelectItem>
                    ))}
                  </MultiSelectContent>
                </MultiSelect>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium text-gray-700">Format</Label>
              <Select value={downloadFormat} onValueChange={handleChangeSelectedFormat}>
                <SelectTrigger>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {FORMAT_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {showSourceDocumentsOption && (
              <div className="my-4 flex flex-col gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={cn('flex items-center gap-3', !canDownloadDocs && 'opacity-50')}>
                      <Checkbox
                        id="includeSourceDocuments"
                        checked={canDownloadDocs ? includeSourceDocuments : false}
                        disabled={!canDownloadDocs}
                        onCheckedChange={(checked) => setIncludeSourceDocuments(Boolean(checked))}
                      />
                      <label
                        htmlFor="includeSourceDocuments"
                        className={cn('cursor-pointer text-sm text-gray-700', !canDownloadDocs && 'cursor-not-allowed')}
                      >
                        <div className="font-medium">Include source documents</div>
                        <div className="text-xs text-gray-500">
                          Download a zip containing the chronology and all referenced documents
                        </div>
                      </label>
                    </div>
                  </TooltipTrigger>
                  {!canDownloadDocs && (
                    <TooltipContent>
                      <p>You do not have permission to download source documents</p>
                    </TooltipContent>
                  )}
                </Tooltip>
                {showEmailNotification && includeSourceDocuments && (
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <Icon className="lucide--mail size-3.5 text-gray-500" />
                    You will receive an email when the export is ready.
                  </div>
                )}
              </div>
            )}

            <div className="my-6 border-t border-gray-200"></div>

            <Button onClick={handleDownloadClick} variant="primary" className="w-full">
              Download
              <Icon className="lucide--download" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
